import { config } from '@/config';
import { DefaultPageProps, ICountryData } from '@/types/types';
import { getErrorMessage } from './getErrorMessage';
import { Entries, ValueOf } from '@/types/helpers';
import { isArray, isObject } from '@/types';

export const dictionaryEndpoints = {
    countries: 'dictionary/countries',
    howHeardList: 'registration/sources',
    professions: 'dictionary/professions',
    specialties: 'dictionary/specialties',
    timezones: 'dictionary/timezones',
    surveyBadges: 'dictionary/survey-badges',
    supportRequestGroups: 'dictionary/support-request-types',
} as const;

export const getDictionary = async (): Promise<DefaultPageProps['dictionary']> => {
    type DictionaryEndpoint = ValueOf<typeof dictionaryEndpoints>;

    const getDictionaryItem = async (endpoint: DictionaryEndpoint) => {
        const url = `${config.apiBasePath}/api/${endpoint}`;
        const tags = [endpoint, 'dictionary'];

        try {
            const res = await fetch(url, {
                next: { tags, revalidate: config.timeout.revalidate },
            });
            const json: unknown = await res.json();
            const isValidDictionaryItem = (v: unknown): v is { data: [] } =>
                isObject(v) && 'data' in v && isArray(v.data);

            if (isValidDictionaryItem(json)) {
                let data;
                if (endpoint === 'dictionary/countries') {
                    data = (json.data as ICountryData[]).filter(
                        (item) => item.countryCode !== 'RUS',
                    );
                } else {
                    data = json.data;
                }
                return data;
            } else if (res.ok) {
                return json;
            }
        } catch (err) {
            const errorMessage = `Fetch failed for dictionary item with endpoint ${endpoint}: ${getErrorMessage(
                err,
            )}`;
            if (err && isObject(err) && 'message' in err) {
                err.message = errorMessage;
            }
            throw err;
        }
    };

    const dictionaryItems = (await Promise.all(
        (Object.entries(dictionaryEndpoints) as Entries<typeof dictionaryEndpoints>).map(
            async ([title, endpoint]) => ({
                [title]: await getDictionaryItem(endpoint),
            }),
        ),
    )) as unknown as Record<keyof typeof dictionaryEndpoints, []>[];

    return Object.assign({}, ...dictionaryItems) as DefaultPageProps['dictionary'];
};
